import {
  getDownloadURL,
  ref
} from 'firebase/storage';
import { useEffect, useMemo, useState } from 'react';
import { updatePassword } from 'firebase/auth';
import {
  BookOpenCheck,
  CalendarDays,
  ChevronRight,
  GraduationCap,
  Pencil,
  UserRound
} from 'lucide-react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where
} from 'firebase/firestore';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../../core/auth/AuthProvider';
import { auth, db, storage } from '../../core/firebase/firebase';
import TeacherProfileForm, { TEACHER_ONBOARDING_VERSION } from './TeacherProfileForm';
import TeacherShell from './TeacherShell';
import TeacherRequestsPage from './TeacherRequestsPage';
import TeacherContractPage from './TeacherContractPage';

const MARITAL_LABELS = {
  single: 'Célibataire',
  married: 'Marié(e)',
  divorced: 'Divorcé(e)',
  widowed: 'Veuf / veuve',
  other: 'Autre'
};

function displayDate(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : value || 'Non renseignée';
}

function SectionTitle({ eyebrow, title, description }) {
  return (
    <div className="parent-section-title">
      <p className="eyebrow">{eyebrow}</p>
      <h2>{title}</h2>
      {description && <p>{description}</p>}
    </div>
  );
}

export default function TeacherPortal() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, profile, refreshProfile } = useAuth();

  const [assignments, setAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(true);
  const [assignmentsError, setAssignmentsError] = useState('');
  const [timetable, setTimetable] = useState([]);
  const [, setLoadingTimetable] = useState(true);
const [, setTimetableError] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstAccessError, setFirstAccessError] = useState('');
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [notice, setNotice] = useState('');

  const [teacherTimetableUrl, setTeacherTimetableUrl] = useState('');
const [teacherTimetableLoading, setTeacherTimetableLoading] = useState(false);

useEffect(() => {
  let cancelled = false;

  async function loadTeacherTimetable() {
    if (!user?.uid) {
      setTeacherTimetableUrl('');
      setTeacherTimetableLoading(false);
      return;
    }

    setTeacherTimetableLoading(true);

    try {
      const fileRef = ref(
        storage,
        `teacherTimetables/${user.uid}/emploi`
      );

      const url = await getDownloadURL(fileRef);

      if (!cancelled) {
        setTeacherTimetableUrl(url);
      }
    } catch (err) {
      if (err?.code === 'storage/object-not-found') {
        if (!cancelled) {
          setTeacherTimetableUrl('');
        }
      } else {
        console.error(
          'Chargement emploi enseignant :',
          err
        );

        if (!cancelled) {
          setTeacherTimetableUrl('');
        }
      }
    } finally {
      if (!cancelled) {
        setTeacherTimetableLoading(false);
      }
    }
  }

  loadTeacherTimetable();

  return () => {
    cancelled = true;
  };
}, [user?.uid]);

  const mustChangePassword = profile?.mustChangePassword === true;
  const onboardingCompleted =
    Number(profile?.teacherOnboardingVersion || 0) >= TEACHER_ONBOARDING_VERSION &&
    Number(profile?.teacherNotice?.schemaVersion || 0) >= TEACHER_ONBOARDING_VERSION &&
    profile?.teacherNotice?.confirmationAccepted === true;
  const accessReady = !mustChangePassword && onboardingCompleted;

  const teacherNotice = profile?.teacherNotice || {};
  const teacherName =
    [teacherNotice.firstName, teacherNotice.lastName].filter(Boolean).join(' ') ||
    profile?.fullname ||
    profile?.fullName ||
    profile?.displayName ||
    'Enseignant';

  const requestedView = searchParams.get('view');
  const activePage = ['classes', 'timetable', 'requests', 'contract', 'profile'].includes(requestedView)
    ? requestedView
    : 'dashboard';

  const groupedClasses = useMemo(() => {
    const map = new Map();
    assignments.forEach((assignment) => {
      if (!map.has(assignment.classId)) {
        map.set(assignment.classId, {
          id: assignment.classId,
          className: assignment.className,
          subjects: []
        });
      }
      const row = map.get(assignment.classId);
      if (assignment.subjectName && !row.subjects.includes(assignment.subjectName)) {
        row.subjects.push(assignment.subjectName);
      }
    });
    return Array.from(map.values());
  }, [assignments]);

  useEffect(() => {
    if (!user?.uid || !accessReady) return;
    let cancelled = false;

    async function loadAssignments() {
      setLoadingAssignments(true);
      setAssignmentsError('');
      try {
        const snapshot = await getDocs(
          query(collection(db, 'teacherAssignments'), where('teacherId', '==', user.uid))
        );
        const active = snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .filter((item) => item.status === 'active');

        const enriched = await Promise.all(
          active.map(async (assignment) => {
            const [classSnapshot, subjectSnapshot] = await Promise.all([
              getDoc(doc(db, 'classes', assignment.classId)),
              getDoc(doc(db, 'subjects', assignment.subjectId))
            ]);
            const classData = classSnapshot.exists() ? classSnapshot.data() : {};
            const subjectData = subjectSnapshot.exists() ? subjectSnapshot.data() : {};
            return {
              ...assignment,
              className: classData.name || classData.className || assignment.className || assignment.classId,
              subjectName: subjectData.name || subjectData.subjectName || assignment.subjectName || assignment.subjectId
            };
          })
        );
        if (!cancelled) setAssignments(enriched);
      } catch (error) {
        console.error('Erreur chargement affectations enseignant :', error);
        if (!cancelled) setAssignmentsError('Impossible de charger vos classes pour le moment.');
      } finally {
        if (!cancelled) setLoadingAssignments(false);
      }
    }

    async function loadTimetable() {
      setLoadingTimetable(true);
      setTimetableError('');
      try {
        const snapshot = await getDocs(
          query(collection(db, 'timetables'), where('teacherId', '==', user.uid))
        );
        const rows = snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .filter((item) => item.status !== 'inactive')
          .sort((a, b) => {
            const dayDiff = Number(a.dayOrder || 99) - Number(b.dayOrder || 99);
            return dayDiff || String(a.startTime || '').localeCompare(String(b.startTime || ''));
          });
        if (!cancelled) setTimetable(rows);
      } catch (error) {
        console.error('Erreur chargement emploi du temps enseignant :', error);
        if (!cancelled) setTimetableError('Impossible de charger votre emploi du temps.');
      } finally {
        if (!cancelled) setLoadingTimetable(false);
      }
    }

    loadAssignments();
    loadTimetable();
    return () => { cancelled = true; };
  }, [user?.uid, accessReady]);

  async function changePassword(event) {
    event.preventDefault();
    setFirstAccessError('');
    if (newPassword.length < 8) {
      setFirstAccessError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setFirstAccessError('Les mots de passe ne correspondent pas.');
      return;
    }

    setPasswordSubmitting(true);
    try {
      await updatePassword(auth.currentUser, newPassword);
      await updateDoc(doc(db, 'users', user.uid), {
        mustChangePassword: false,
        passwordChangedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      await refreshProfile();
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Changement mot de passe enseignant :', error);
      setFirstAccessError('Impossible de modifier le mot de passe. Reconnectez-vous puis réessayez.');
    } finally {
      setPasswordSubmitting(false);
    }
  }

  if (mustChangePassword) {
    return (
      <main className="teacher-first-access-screen">
        <section className="teacher-first-access-card">
          <div className="teacher-first-access-mark"><GraduationCap size={28} /></div>
          <p className="eyebrow">PREMIÈRE CONNEXION</p>
          <h1>Créez votre mot de passe</h1>
          <p className="muted">Pour sécuriser votre compte, remplacez le mot de passe temporaire fourni par l’administration.</p>
          <form className="form-stack" onSubmit={changePassword}>
            <label>Nouveau mot de passe
              <input type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} required minLength={8} autoComplete="new-password" />
            </label>
            <label>Confirmer le nouveau mot de passe
              <input type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} required minLength={8} autoComplete="new-password" />
            </label>
            {firstAccessError && <div className="alert alert-error">{firstAccessError}</div>}
            <button className="button button-primary" type="submit" disabled={passwordSubmitting}>
              {passwordSubmitting ? 'Enregistrement…' : 'Enregistrer mon mot de passe'}
            </button>
          </form>
        </section>
      </main>
    );
  }

  if (!onboardingCompleted) {
    return <TeacherProfileForm profile={profile} user={user} onSaved={refreshProfile} />;
  }

  function go(view) {
    setEditingProfile(false);
    setNotice('');
    if (view === 'dashboard') setSearchParams({});
    else setSearchParams({ view });
  }

  function renderClasses() {
    return (
      <section>
        <SectionTitle eyebrow="PÉDAGOGIE" title="Mes classes" description="Retrouvez les classes et matières qui vous sont affectées." />
        {assignmentsError && <div className="alert alert-error">{assignmentsError}</div>}
        {loadingAssignments ? <div className="parent-empty large">Chargement de vos classes…</div> : (
          <div className="parent-children-grid-v0">
            {groupedClasses.map((item) => (
              <button key={item.id} type="button" className="parent-child-card-v0" onClick={() => navigate(`/enseignant/classes/${item.id}`)}>
                <div className="child-avatar large">{String(item.className || 'C').charAt(0).toUpperCase()}</div>
                <div><strong>{item.className}</strong><span>{item.subjects.join(' • ') || 'Matière à confirmer'}</span><small>Voir les élèves et les présences</small></div>
                <ChevronRight />
              </button>
            ))}
            {!groupedClasses.length && <div className="parent-empty large">Aucune classe ne vous est affectée pour le moment.</div>}
          </div>
        )}
      </section>
    );
  }

  function renderTimetable() {
  return (
    <section>
      <SectionTitle
        eyebrow="ORGANISATION"
        title="Emploi du temps"
        description="Consultez votre emploi du temps."
      />

      {teacherTimetableLoading && (
        <div className="parent-empty large">
          Chargement de l'emploi du temps…
        </div>
      )}

      {!teacherTimetableLoading && teacherTimetableUrl && (
        <article className="parent-card-v0 parent-timetable-image-card">
          <img
            src={teacherTimetableUrl}
            alt="Emploi du temps enseignant"
          />
        </article>
      )}

      {!teacherTimetableLoading && !teacherTimetableUrl && (
        <div className="parent-empty large">
          Aucun emploi du temps n'a encore été publié.
        </div>
      )}
    </section>
  );
}

  function renderProfile() {
    if (editingProfile) {
      return (
        <TeacherProfileForm
          mode="edit"
          profile={profile}
          user={user}
          onCancel={() => setEditingProfile(false)}
          onSaved={async () => {
            await refreshProfile();
            setEditingProfile(false);
            setNotice('Vos informations ont été mises à jour.');
          }}
        />
      );
    }

    const marital = teacherNotice.maritalStatus === 'other'
      ? teacherNotice.maritalStatusOther || 'Autre'
      : MARITAL_LABELS[teacherNotice.maritalStatus] || 'Non renseignée';

    return (
      <section>
        <div className="parent-profile-heading-v1">
          <SectionTitle eyebrow="MON COMPTE" title="Ma notice individuelle" description="Consultez et mettez à jour les informations communiquées à l’administration." />
          <button className="button button-primary" type="button" onClick={() => setEditingProfile(true)}><Pencil size={16} /> Modifier mes informations</button>
        </div>

        {notice && <div className="alert parent-notice-v0">{notice}<button type="button" onClick={() => setNotice('')}>×</button></div>}

        <div className="parent-profile-grid-v1">
          <article className="parent-card-v0 parent-profile-card-v1">
            <div className="parent-card-v0-heading"><div><p className="eyebrow">IDENTITÉ</p><h3>{teacherName}</h3></div></div>
            <dl className="parent-details">
              <div><dt>Genre</dt><dd>{teacherNotice.gender === 'female' ? 'Féminin' : teacherNotice.gender === 'male' ? 'Masculin' : 'Non renseigné'}</dd></div>
              <div><dt>CIN</dt><dd>{teacherNotice.cin || 'Non renseignée'}</dd></div>
              <div><dt>Date de naissance</dt><dd>{displayDate(teacherNotice.birthDate)}</dd></div>
              <div><dt>Lieu de naissance</dt><dd>{teacherNotice.birthPlace || 'Non renseigné'}</dd></div>
            </dl>
          </article>

          <article className="parent-card-v0 parent-profile-card-v1">
            <div className="parent-card-v0-heading"><div><p className="eyebrow">COORDONNÉES</p><h3>Contact</h3></div></div>
            <dl className="parent-details">
              <div><dt>Téléphone</dt><dd>{teacherNotice.phone || profile?.phone || 'Non renseigné'}</dd></div>
              <div><dt>Email personnel</dt><dd>{teacherNotice.personalEmail || profile?.personalEmail || 'Non renseigné'}</dd></div>
              <div><dt>Adresse</dt><dd>{teacherNotice.address || 'Non renseignée'}</dd></div>
            </dl>
          </article>

          <article className="parent-card-v0 parent-profile-card-v1">
            <div className="parent-card-v0-heading"><div><p className="eyebrow">FAMILLE</p><h3>Situation familiale</h3></div></div>
            <dl className="parent-details">
              <div><dt>Statut</dt><dd>{marital}</dd></div>
              <div><dt>Nombre d’enfants</dt><dd>{teacherNotice.childrenCount ?? 0}</dd></div>
              {teacherNotice.maritalStatus === 'married' && <>
                <div><dt>Conjoint</dt><dd>{teacherNotice.spouseFullName || 'Non renseigné'}</dd></div>
                <div><dt>Profession du conjoint</dt><dd>{teacherNotice.spouseProfession || 'Non renseignée'}</dd></div>
              </>}
            </dl>
          </article>

          <article className="parent-card-v0 parent-profile-card-v1">
            <div className="parent-card-v0-heading"><div><p className="eyebrow">PARCOURS</p><h3>Formation</h3></div></div>
            <dl className="parent-details">
              <div><dt>Diplôme</dt><dd>{teacherNotice.diploma || 'Non renseigné'}</dd></div>
              <div><dt>Spécialité</dt><dd>{teacherNotice.studyField || 'Non renseignée'}</dd></div>
              <div><dt>Établissement</dt><dd>{teacherNotice.institution || 'Non renseigné'}</dd></div>
              <div><dt>Année d’obtention</dt><dd>{teacherNotice.graduationYear || 'Non renseignée'}</dd></div>
            </dl>
          </article>

          <article className="parent-card-v0 parent-profile-card-v1 span-two">
            <div className="parent-card-v0-heading"><div><p className="eyebrow">DOSSIER ADMINISTRATIF</p><h3>CNSS et coordonnées bancaires</h3></div></div>
            <dl className="parent-details teacher-admin-details-v1">
              <div><dt>Numéro CNSS</dt><dd>{teacherNotice.cnssNumber || 'Non renseigné'}</dd></div>
              <div><dt>Banque</dt><dd>{teacherNotice.bankName || 'Non renseignée'}</dd></div>
              <div><dt>RIB</dt><dd>{teacherNotice.rib || 'Non renseigné'}</dd></div>
            </dl>
          </article>
        </div>
      </section>
    );
  }

  function renderDashboard() {
    return (
      <>
        <section className="parent-hero-v0 teacher-hero-v1">
          <div>
            <p className="eyebrow">ESPACE ENSEIGNANT</p>
            <h1>Bonjour {teacherNotice.firstName || teacherName}</h1>
            <p>Retrouvez vos classes, votre emploi du temps et vos activités pédagogiques.</p>
          </div>
          <div className="teacher-hero-badge-v1"><UserRound size={20} /><span>Dossier complété</span></div>
        </section>

        <section className="parent-v0-kpis teacher-kpis-v1">
          <button type="button" onClick={() => go('classes')}><GraduationCap /><div><strong>{groupedClasses.length}</strong><span>Classe{groupedClasses.length > 1 ? 's' : ''}</span></div></button>
          <button type="button" onClick={() => go('timetable')}><CalendarDays /><div><strong>{timetable.length}</strong><span>Séance{timetable.length > 1 ? 's' : ''}</span></div></button>
          <button type="button" onClick={() => navigate('/enseignant/travail')}><BookOpenCheck /><div><strong>Travaux</strong><span>Créer et suivre</span></div></button>
          <button type="button" onClick={() => go('profile')}><UserRound /><div><strong>Profil</strong><span>Voir ou modifier</span></div></button>
        </section>

        <section className="parent-dashboard-grid-v0">
          <article className="parent-card-v0 span-two">
            <div className="parent-card-v0-heading"><div><p className="eyebrow">MES CLASSES</p><h3>Accès rapide</h3></div><button type="button" onClick={() => go('classes')}>Tout voir</button></div>
            {loadingAssignments ? <p className="muted">Chargement…</p> : groupedClasses.slice(0, 4).map((item) => (
              <button key={item.id} type="button" className="teacher-dashboard-row-v1" onClick={() => navigate(`/enseignant/classes/${item.id}`)}>
                <div className="parent-list-icon"><GraduationCap /></div>
                <div><strong>{item.className}</strong><span>{item.subjects.join(' • ') || 'Matière à confirmer'}</span></div>
                <ChevronRight />
              </button>
            ))}
            {!loadingAssignments && !groupedClasses.length && <div className="parent-empty">Aucune classe attribuée.</div>}
          </article>

          <article className="parent-card-v0">
  <div className="parent-card-v0-heading">
    <div>
      <p className="eyebrow">EMPLOI DU TEMPS</p>
      <h3>Mon emploi du temps</h3>
    </div>

    <button
      type="button"
      onClick={() => go('timetable')}
    >
      Tout voir
    </button>
  </div>

  {teacherTimetableLoading && (
    <div className="parent-empty">
      Chargement de l'emploi du temps…
    </div>
  )}

  {!teacherTimetableLoading && teacherTimetableUrl && (
    <div className="teacher-dashboard-timetable-preview">
      <img
        src={teacherTimetableUrl}
        alt="Emploi du temps enseignant"
      />
    </div>
  )}

  {!teacherTimetableLoading && !teacherTimetableUrl && (
    <div className="parent-empty">
      Aucun emploi du temps n'a encore été publié.
    </div>
  )}
</article>
        </section>
      </>
    );
  }

  return (
    <TeacherShell activePage={activePage}>
      {activePage === 'classes' && renderClasses()}
      {activePage === 'timetable' && renderTimetable()}
      {activePage === 'requests' && <TeacherRequestsPage />}
      {activePage === 'contract' && <TeacherContractPage />}
      {activePage === 'profile' && renderProfile()}
      {activePage === 'dashboard' && renderDashboard()}
    </TeacherShell>
  );
}
